import { useEffect, useMemo, useState } from 'react';
import { Loader2, Check, AlertTriangle, RotateCcw } from 'lucide-react';
import { YourCheckaNavigation } from '@/components/Navbar';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { useGetAdminPlansQuery, useGetPricingQuery, useUpdatePricingMutation } from '@/store/api/apiSlice';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import type { PricingConfig, UpdatePricingRequest } from '@/types';
import CheckAddonsManager from './CheckAddonsManager';
import PlanFeaturesManager from './PlanFeaturesManager';

function getInitials(firstName?: string, lastName?: string) {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'A';
}

function formatNaira(value: number) {
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(
        value || 0,
    );
}

// The editable numeric fields, grouped for the form.
type Field = keyof UpdatePricingRequest;

type FieldDef = {
    key: Field;
    label: string;
    hint: string;
    unit?: 'naira' | 'percent';
    // Plan this field prices. When set, the field is labelled with the plan's
    // live (admin-editable) name instead of `label`, so this screen can never
    // disagree with the Plans screen about which tier is which.
    planId?: string;
};

const SECTIONS: { title: string; description: string; fields: FieldDef[] }[] = [
    {
        title: 'Whole plan saving',
        description:
            'Features carry standalone prices, set in the catalog below. A plan is sold at the sum of its features less this, which is what makes buying the whole plan worth it.',
        fields: [
            {
                key: 'wholePlanDiscountPercent',
                label: 'Whole plan discount',
                hint: '0 removes the saving, and the reason to buy the plan whole',
                unit: 'percent',
            },
        ],
    },
    {
        title: 'Earnings',
        description: 'What staff earn per investigation on report approval. Set to 0 to disable.',
        fields: [
            { key: 'agentFlatEarning', label: 'Agent flat earning', hint: 'Per investigation' },
            { key: 'reassignedGuarantorFee', label: 'Reassigned guarantor fee', hint: 'Moved to the in-town agent' },
            { key: 'customerSuccessEarning', label: 'Customer Success earning', hint: '0 = disabled' },
            { key: 'medicalEarning', label: 'Medical earning', hint: '0 = disabled' },
        ],
    },
];

const ALL_FIELDS: Field[] = SECTIONS.flatMap((s) => s.fields.map((f) => f.key));

// Percent fields are capped at 100; everything else is an unbounded naira amount.
const PERCENT_FIELDS = new Set<Field>(
    SECTIONS.flatMap((s) => s.fields.filter((f) => f.unit === 'percent').map((f) => f.key)),
);

function isValidValue(key: Field, raw: string): boolean {
    const trimmed = raw.trim();
    if (trimmed === '') return false;
    const num = Number(trimmed);
    if (!Number.isInteger(num) || num < 0) return false;
    return !PERCENT_FIELDS.has(key) || num <= 100;
}

export default function Pricing() {
    const currentUser = useAppSelector(selectCurrentUser);
    const { data: pricing, isLoading, error: loadError } = useGetPricingQuery();
    // Plan names are admin-editable on the Plans screen; read them here so a
    // renamed tier is labelled the same on both screens.
    const { data: plans } = useGetAdminPlansQuery();
    const [updatePricing, { isLoading: isSaving }] = useUpdatePricingMutation();

    const [form, setForm] = useState<Record<Field, string>>(() => emptyForm());
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Hydrate the form whenever the config loads/refreshes.
    useEffect(() => {
        if (pricing) setForm(toForm(pricing));
    }, [pricing]);

    // Only the fields the admin actually changed (and that parse to a valid int ≥ 0).
    const changed = useMemo<UpdatePricingRequest>(() => {
        if (!pricing) return {};
        const diff: UpdatePricingRequest = {};
        ALL_FIELDS.forEach((key) => {
            const raw = form[key].trim();
            if (!isValidValue(key, raw)) return;
            const num = Number(raw);
            if (num !== pricing[key]) diff[key] = num;
        });
        return diff;
    }, [form, pricing]);

    const hasInvalid = ALL_FIELDS.some((key) => !isValidValue(key, form[key]));

    const dirtyCount = Object.keys(changed).length;

    // Live plan name, falling back to the built-in label until the plans load.
    const fieldLabel = (field: FieldDef) =>
        (field.planId && plans?.find((p) => p.id === field.planId)?.name) || field.label;

    const handleChange = (key: Field, value: string) => {
        // Allow digits only.
        setForm((prev) => ({ ...prev, [key]: value.replace(/[^\d]/g, '') }));
        setSuccess('');
    };

    const handleReset = () => {
        if (pricing) setForm(toForm(pricing));
        setError('');
        setSuccess('');
    };

    const handleSave = async () => {
        setError('');
        setSuccess('');
        if (hasInvalid) {
            setError('Every amount must be a whole number of ₦0 or more, and the bundle discount 0–100%.');
            return;
        }
        if (dirtyCount === 0) return;
        try {
            await updatePricing(changed).unwrap();
            setSuccess('Pricing updated. New checkouts and future earnings use these values.');
        } catch (err) {
            setError(parseErrorMessage(err, 'Failed to update pricing.'));
        }
    };

    return (
        <main className="w-full min-h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
            <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
                <YourCheckaNavigation
                    showTitle={false}
                    showInfo={false}
                    showLocation={false}
                    notificationCount={0}
                    userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Admin'}
                    userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'A'}
                    only={true}
                    className="border-b border-gray-100"
                />

                <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-10 flex flex-col gap-6 w-full">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
                        <div>
                            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">Pricing</h1>
                            <p className="text-sm text-gray-500 mt-1">
                                Plan prices, the add-on catalog, and staff earnings. Changes apply to new checkouts and
                                future report approvals.
                            </p>
                        </div>
                        {pricing && (
                            <p className="text-xs text-gray-400">
                                Last updated{' '}
                                {new Date(pricing.updatedAt).toLocaleString('en-GB', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                })}
                            </p>
                        )}
                    </div>

                    {isLoading ? (
                        <div className="flex items-center justify-center py-20">
                            <Loader2 className="h-8 w-8 text-[#FF6B35] animate-spin" />
                        </div>
                    ) : loadError || !pricing ? (
                        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                            <AlertTriangle className="w-4 h-4 shrink-0" />
                            Failed to load pricing config.
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {SECTIONS.map((section) => (
                                    <div
                                        key={section.title}
                                        className="rounded-2xl border border-gray-100 p-5 flex flex-col gap-4"
                                    >
                                        <div>
                                            <h3 className="text-sm font-bold text-gray-900">{section.title}</h3>
                                            <p className="text-xs text-gray-500 mt-0.5">{section.description}</p>
                                        </div>
                                        <div className="flex flex-col gap-4">
                                            {section.fields.map((field) => (
                                                <PriceField
                                                    key={field.key}
                                                    label={fieldLabel(field)}
                                                    hint={field.hint}
                                                    unit={field.unit ?? 'naira'}
                                                    value={form[field.key]}
                                                    onChange={(v) => handleChange(field.key, v)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <PlanFeaturesManager />

                            <CheckAddonsManager />

                            {error && (
                                <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                    {error}
                                </div>
                            )}
                            {success && (
                                <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                                    <Check className="w-4 h-4 shrink-0" />
                                    {success}
                                </div>
                            )}

                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={isSaving || dirtyCount === 0 || hasInvalid}
                                    className="bg-[#FF6B35] hover:bg-[#FF5722] disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 rounded-xl text-sm font-medium flex items-center gap-2"
                                >
                                    {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                                    {dirtyCount > 0 ? `Save ${dirtyCount} change${dirtyCount > 1 ? 's' : ''}` : 'Save'}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleReset}
                                    disabled={isSaving || dirtyCount === 0}
                                    className="border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 px-4 py-3 rounded-xl text-sm font-medium flex items-center gap-2"
                                >
                                    <RotateCcw className="w-4 h-4" />
                                    Reset
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </main>
    );
}

function PriceField({
    label,
    hint,
    unit = 'naira',
    value,
    onChange,
}: {
    label: string;
    hint: string;
    unit?: 'naira' | 'percent';
    value: string;
    onChange: (value: string) => void;
}) {
    const num = Number(value);
    const isValid = value.trim() !== '' && Number.isInteger(num) && num >= 0;
    const preview = isValid ? (unit === 'percent' ? `${num}%` : formatNaira(num)) : null;
    return (
        <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-800">{label}</label>
            <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                    {unit === 'percent' ? '%' : '₦'}
                </span>
                <input
                    type="text"
                    inputMode="numeric"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#FF6B35]"
                />
            </div>
            <span className="text-[11px] text-gray-400">
                {hint}
                {preview ? ` · ${preview}` : ''}
            </span>
        </div>
    );
}

function emptyForm(): Record<Field, string> {
    return ALL_FIELDS.reduce((acc, key) => ({ ...acc, [key]: '' }), {} as Record<Field, string>);
}

function toForm(pricing: PricingConfig): Record<Field, string> {
    return ALL_FIELDS.reduce((acc, key) => ({ ...acc, [key]: String(pricing[key] ?? 0) }), {} as Record<Field, string>);
}
